import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import Cookies from "js-cookie";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { toast } from "sonner";
import { AxiosError } from "axios";
import { useEffect } from "react";

import { editAdmin } from "@/config/mutation/super-admin";
import { getSingleAdminQueryOptions } from "@/config/queries/superadmin-query-options";

import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs/tabs";
import { Form } from "@/components/ui/form/form";
import { Button } from "@/components/ui/button/button";
import { AdminDetailsForm } from "./_components/admin-details-form";
import { AdminManagedEmployees } from "./_components/admin-managed-employees";

const formSchema = z.object({
  // ? TAB Overview
  name: z
    .string({
      required_error: "Employee name required!",
    })
    .min(1, {
      message: "Employee name is required!",
    }),
  email: z
    .string({
      required_error: "Email is required!",
    })
    .email({
      message: "Invalid Email",
    }),
  password: z.string().optional(),
  phone: z.string().length(10, {
    message: "Phone number should of length 10 digit",
  }),
});

export type FormValues = z.infer<typeof formSchema>;

export const EditAdminPage = () => {
  const params = useParams() as {
    adminId: string;
  };
  const superAdminKey = Cookies.get("key") as string;

  const queryClient = useQueryClient();

  // 1. Define your form.
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
      email: "",
      password: "",

      phone: "",
    },
  });

  const { mutate, isPending: isEditing } = useMutation({
    mutationFn: editAdmin(superAdminKey),
  });

  // 2. Define a submit handler.
  function onSubmit(values: FormValues) {
    const postData = {
      name: values.name,
      email: values.email,
      phone: values.phone,
      password: values?.password,
    };
    // console.log(postData);

    mutate(
      {
        adminId: params.adminId,
        postData,
      },
      {
        onSuccess: async () => {
          // console.log("New Employee", data);
          await queryClient.invalidateQueries({
            queryKey: ["sa-admins"],
          });
          await queryClient.invalidateQueries({
            queryKey: ["sa-admin", params.adminId],
          });

          toast.success("Details Saved!");
        },

        onError: (error) => {
          console.log("ERROR WHILE Saving admin details", error);
          const isAxiosError = error instanceof AxiosError;
          if (isAxiosError) {
            const errorReason = error?.response?.data?.error as string;
            toast.error(errorReason);
          } else {
            toast.error(error?.message);
          }
        },
      }
    );
  }

  const { data, isPending, isError, error } = useQuery(
    getSingleAdminQueryOptions(superAdminKey, params.adminId)
  );

  useEffect(() => {
    form.setValue("name", data?.admin?.name as string);
    form.setValue("email", data?.admin?.email as string);
    form.setValue("phone", data?.admin?.phone as string);
  }, [data]);

  if (isPending) {
    return <div>Loading....</div>;
  }

  if (isError) {
    return <pre>{JSON.stringify(error)}</pre>;
  }

  return (
    <div className="p-5 md:h-[calc(100%-56px)] h-[calc(100vh-56px)] relative">
      <div className="p-4 flex items-center gap-4">
        <div className="w-20 h-20 rounded-full bg-accent flex items-center justify-center text-xl">
          {data?.admin?.name?.charAt(0)}
        </div>

        <div className="flex flex-col">
          <h3 className="text-2xl font-semibold">{data?.admin?.name}</h3>
          <p className="text-sm">{data?.admin?.email}</p>
        </div>
      </div>
      <div className="bg-card rounded-md p-3">
        <div className="flex flex-col relative">
          <div className="">
            <Tabs defaultValue="details" className="mr-auto w-full">
              <div className="flex items-center justify-between pb-3 sticky top-[56px] bg-card z-30">
                <TabsList className="w-full justify-start rounded-none border-b border-b-gray-500 bg-transparent p-0">
                  <TabsTrigger
                    value="details"
                    className="relative h-9 rounded-none border-b-2 border-b-transparent bg-transparent px-4 pb-3 pt-2 font-semibold text-muted-foreground shadow-none transition-none data-[state=active]:border-b-primary data-[state=active]:text-foreground data-[state=active]:bg-transparent data-[state=active]:shadow-none"
                  >
                    Details
                  </TabsTrigger>
                  <TabsTrigger
                    value="employees"
                    className="relative h-9 rounded-none border-b-2 border-b-transparent bg-transparent px-4 pb-3 pt-2 font-semibold text-muted-foreground shadow-none transition-none data-[state=active]:border-b-primary data-[state=active]:text-foreground data-[state=active]:bg-transparent data-[state=active]:shadow-none"
                  >
                    Managed Employees
                  </TabsTrigger>
                </TabsList>
              </div>

              <TabsContent value="details" className="relative rounded-md">
                <Form {...form}>
                  <form onSubmit={form.handleSubmit(onSubmit)}>
                    <div className="flex justify-end mb-3">
                      <Button type="submit" disabled={isEditing}>
                        Save
                      </Button>
                    </div>
                    <AdminDetailsForm />
                  </form>
                </Form>
              </TabsContent>

              <TabsContent value="employees" className="relative rounded-md">
                <AdminManagedEmployees adminId={params.adminId} />
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </div>
    </div>
  );
};
